import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import Fuzzyurl from "fuzzyurl";
import PropTypes from "prop-types";

import { CButton, CSkeleton } from "@appcues/component-library";
import PublishDropdown from "components/Common/Publishing/PublishDropdown";
import ButtonWithTooltip from "components/Common/ButtonWithTooltip";

import {
  INSTALL_MODAL_TEST_MODE,
  PUBLISHING_MODAL,
  FLOW_PUBLISHING_MODAL,
  TEST_FLOW_MODAL
} from "constants/globals";
import { ACCOUNT_PERMISSIONS } from "constants/accountManagement/permissions";
import { STEP_TYPE_IDS, STEP_TYPES } from "constants/stepTypes";
import { PUBLISH_ACTIONS } from "constants/modalStatuses";
import { navigate } from "actions/routing";
import { showModal } from "actions/currentModal";
import { selectAccountMeta } from "reducers/account/meta";
import { isInstalledAtDomain } from "utils";
import { getContentApiPostPromise } from "helpers/content-api";
import getContentAction from "actions/publishing";
import {
  selectInstalledDomains,
  selectInstalledDomainsSynced
} from "reducers/account/installedDomains";

export const PERMISSIONS_TOOLTIP_TXT =
  "Contact a team admin or publisher to publish.";

export class Publishing extends Component {
  static propTypes = {
    contentStatus: PropTypes.shape({
      publishedAndDirty: PropTypes.bool
    }),
    enableTestMode: PropTypes.bool,
    isInCRX: PropTypes.bool,
    isPublished: PropTypes.bool,
    lastUpdatedAtTimestamp: PropTypes.number,
    showTestOptions: PropTypes.bool,
    stepType: PropTypes.string,
    role: PropTypes.string,
    areInstalledDomainsSynced: PropTypes.bool,
    isDisabled: PropTypes.bool,
    disabledTooltipTxt: PropTypes.string,
    stepId: PropTypes.string,
    installedDomains: PropTypes.arrayOf(PropTypes.string),
    previewUrl: PropTypes.string,
    existingTestFlowId: PropTypes.string,
    flowName: PropTypes.string,
    conditions: PropTypes.object,
    accountMeta: PropTypes.object.isRequired,
    showModal: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
    onAction: PropTypes.func,
    publishToApi: PropTypes.func
  };

  confirmAction = publishActionType => {
    const { stepId, stepType, onAction, publishToApi } = this.props;
    const publishingModalType =
      stepType === STEP_TYPE_IDS.JOURNEY
        ? FLOW_PUBLISHING_MODAL
        : PUBLISHING_MODAL;

    this.props.showModal(publishingModalType, {
      publishActionType,
      stepId,
      onAction,
      publishToApi
    });
  };

  confirmPublishAction = () => {
    this.confirmAction(PUBLISH_ACTIONS.PUBLISH);
  };

  confirmUnpublishAction = () => {
    this.confirmAction(PUBLISH_ACTIONS.UNPUBLISH);
  };

  navigateToEdit = () => {
    this.props.navigate(`nps/${this.props.stepId}/edit`);
  };

  handleTestFlowClick = () => {
    const { installedDomains, previewUrl } = this.props;

    const urlObject = Fuzzyurl.fromString(previewUrl);
    const domain = urlObject.hostname;

    let isInstalledForUrl = isInstalledAtDomain(domain, installedDomains);

    if (isInstalledForUrl) {
      this.showTestFlowModal();
    } else {
      this.showInstallModal();
    }
  };

  showTestFlowModal = () => {
    const {
      existingTestFlowId,
      flowName,
      stepId,
      stepType,
      previewUrl,
      conditions,
      accountMeta
    } = this.props;
    const stepProperties = STEP_TYPES[stepType] || {};
    const contentType = stepProperties.contentType;

    this.props.showModal(TEST_FLOW_MODAL, {
      stepId,
      contentType,
      existingTestFlowId,
      previewUrl,
      conditions,
      flowName,
      accountMeta
    });
  };

  showInstallModal = () => {
    const { accountMeta, stepId } = this.props;
    const { gates, id: accountId } = accountMeta;

    this.props.showModal(INSTALL_MODAL_TEST_MODE, {
      accountId,
      gates,
      stepId
    });
  };

  render() {
    const {
      contentStatus,
      enableTestMode,
      isPublished,
      lastUpdatedAtTimestamp,
      showTestOptions,
      role,
      areInstalledDomainsSynced,
      isDisabled,
      disabledTooltipTxt
    } = this.props;

    if (!areInstalledDomainsSynced) {
      return <CSkeleton width={100} height={40} />;
    }

    const { publishedAndDirty } = contentStatus;
    const cannotPublish = role === ACCOUNT_PERMISSIONS.EDITOR || isDisabled;
    const tooltipTxt =
      isDisabled && disabledTooltipTxt
        ? disabledTooltipTxt
        : PERMISSIONS_TOOLTIP_TXT;

    const lastChangedAt = moment(lastUpdatedAtTimestamp).fromNow();

    const publishOptions = [
      {
        label: "Push Changes",
        value: PUBLISH_ACTIONS.PUBLISH,
        disabled: cannotPublish,
        icon: "fas fa-upload",
        altText: "Push changes option"
      },
      {
        label: "Discard Changes",
        value: PUBLISH_ACTIONS.REVERT,
        disabled: cannotPublish,
        icon: "fas fa-times",
        altText: "Discard changes option"
      },
      {
        label: "Unpublish",
        value: PUBLISH_ACTIONS.UNPUBLISH,
        disabled: cannotPublish,
        icon: "fas fa-level-down-alt",
        altText: "Unpublish flow option"
      }
    ];

    const coolTipProps = {
      marginRight: "18px"
    };

    return (
      <React.Fragment>
        {showTestOptions && enableTestMode && (
          <CButton
            type="warning"
            href="javascript:void(0)"
            onClick={this.handleTestFlowClick}
          >
            Test
          </CButton>
        )}
        {!isPublished && !publishedAndDirty && (
          <ButtonWithTooltip
            isDisabled={cannotPublish}
            buttonText="Publish"
            tooltipText={tooltipTxt}
            onClick={this.confirmPublishAction}
            coolTipProps={coolTipProps}
          />
        )}
        {isPublished && !publishedAndDirty && (
          <CButton
            isDisabled={cannotPublish}
            onClick={this.confirmUnpublishAction}
          >
            Unpublish
          </CButton>
        )}
        {publishedAndDirty && (
          <PublishDropdown
            options={publishOptions}
            confirmAction={this.confirmAction}
            optionDetail={lastChangedAt}
            publishDisabled={cannotPublish}
            isPublished={isPublished}
            disabledTooltipTxt={tooltipTxt}
          />
        )}
      </React.Fragment>
    );
  }
}

Publishing.defaultProps = {
  enableTestMode: false,
  flowName: "",
  previewUrl: "",
  showTestOptions: false,
  publishToApi: getContentApiPostPromise
};

function mapStateToProps(state) {
  return {
    accountMeta: selectAccountMeta(state),
    areInstalledDomainsSynced: selectInstalledDomainsSynced(state),
    installedDomains: selectInstalledDomains(state)
  };
}

const mapDispatchToProps = {
  showModal,
  navigate,
  onAction: (actionType, { content_id, content_type }) =>
    getContentAction(actionType, { id: content_id, type: content_type })
};

export default connect(mapStateToProps, mapDispatchToProps)(Publishing);
